import { useState, useEffect } from "react";
import { useI18n } from "@/i18n/I18nProvider";
import { Button } from "@/components/ui/button";
import { Save, Eye, EyeOff, Plus, Trash2, ArrowUp, ArrowDown, Settings } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { getCmsLayout, saveCmsLayoutDraft, publishCmsLayout, CmsLayout } from "@/data/cmsStore";
import { useToast } from "@/components/ui/use-toast";
import ElectoralMap from "@/components/ElectoralMap";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { regionsMapData } from "@/data/mapData";

const AdminMapPage = () => {
  const { lang, isRtl } = useI18n();
  const { toast } = useToast();
  
  const [layout, setLayout] = useState<CmsLayout>([]);
  const [isMapVisible, setIsMapVisible] = useState(true);
  const [mapContent, setMapContent] = useState<Record<string, string>>({});
  
  // local state for buttons and region stats
  const [customLinks, setCustomLinks] = useState<{label_fr: string, label_ar: string, url: string}[]>([]);
  const [selectedRegionId, setSelectedRegionId] = useState<string>("TTA");
  const [regionStats, setRegionStats] = useState<Record<string, {title_fr: string, title_ar: string, value_fr: string, value_ar: string}[]>>({});

  useEffect(() => {
    loadDraft();
  }, []);

  const loadDraft = () => {
    const draft = getCmsLayout("home", true); // Load draft explicitly
    setLayout(draft);
    
    const mapSec = draft.find(s => s.type === "ElectoralMap");
    if (mapSec) {
      setIsMapVisible(mapSec.visible);
      setMapContent(mapSec.content || {});
      try {
        setCustomLinks(JSON.parse(mapSec.content.custom_links || "[]"));
      } catch (e) {
        setCustomLinks([]);
      }
      try {
        setRegionStats(JSON.parse(mapSec.content.region_stats || "{}"));
      } catch (e) {
        setRegionStats({});
      }
    }
  };

  const handleSaveDraft = () => {
    // stringify complex fields
    const contentToSave = { 
        ...mapContent, 
        custom_links: JSON.stringify(customLinks),
        region_stats: JSON.stringify(regionStats)
    };
    
    const newLayout = layout.map(s => {
      if (s.type === "ElectoralMap") {
        return { ...s, visible: isMapVisible, content: contentToSave };
      }
      return s;
    });
    
    setLayout(newLayout);
    saveCmsLayoutDraft("home", newLayout);
    
    toast({
      title: "Brouillon sauvegardé",
      description: "Les modifications sont appliquées à l'aperçu.",
    });
  };

  const handlePublish = () => {
    handleSaveDraft();
    // Re-fetch to ensure we have the absolute latest state to publish
    const draft = getCmsLayout("home", true);
    publishCmsLayout("home", draft);
    
    toast({
      title: "Publié en ligne",
      description: "La carte électorale est désormais visible pour tous les utilisateurs.",
      variant: "default"
    });
  };

  const updateContent = (key: string, value: string) => {
    setMapContent(prev => ({ ...prev, [key]: value }));
  };
  
  const addLink = () => {
    setCustomLinks([...customLinks, { label_fr: "", label_ar: "", url: "" }]);
  };
  
  const updateLink = (idx: number, key: string, val: string) => {
    const updated = [...customLinks];
    updated[idx] = { ...updated[idx], [key]: val };
    setCustomLinks(updated);
  };
  
  const removeLink = (idx: number) => {
    const updated = customLinks.filter((_, i) => i !== idx);
    setCustomLinks(updated);
  };
  
  // Custom Stats Logic
  const getCurrentRegionStats = () => {
    return regionStats[selectedRegionId] || [];
  };

  const addStatToRegion = () => {
    const current = getCurrentRegionStats();
    setRegionStats({
      ...regionStats,
      [selectedRegionId]: [...current, { title_fr: "", title_ar: "", value_fr: "", value_ar: "" }]
    });
  };

  const updateStat = (idx: number, key: string, val: string) => {
    const current = [...getCurrentRegionStats()];
    current[idx] = { ...current[idx], [key]: val };
    setRegionStats({
      ...regionStats,
      [selectedRegionId]: current
    });
  };

  const removeStat = (idx: number) => {
    const current = getCurrentRegionStats().filter((_, i) => i !== idx);
    setRegionStats({
      ...regionStats,
      [selectedRegionId]: current
    });
  };

  const mapIndex = layout.findIndex(s => s.type === "ElectoralMap");

  const moveMap = (direction: -1 | 1) => {
    if (mapIndex === -1) return;
    const newIndex = mapIndex + direction;
    if (newIndex < 0 || newIndex >= layout.length) return;
    
    const newLayout = [...layout];
    const temp = newLayout[mapIndex];
    newLayout[mapIndex] = newLayout[newIndex];
    newLayout[newIndex] = temp;
    
    setLayout(newLayout);
    saveCmsLayoutDraft("home", newLayout);
  };

  return (
    <div className="space-y-6" dir={isRtl ? "rtl" : "ltr"}>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-display font-bold">Gestion Carte Électorale</h1>
          <p className="text-muted-foreground">Configurez l'aspect, le placement et les données de la carte interactive.</p>
        </div>
        <div className="flex gap-3">
          <Button variant="outline" onClick={handleSaveDraft} className="gap-2">
            <Save className="w-4 h-4" /> Sauvegarder Brouillon
          </Button>
          <Button onClick={handlePublish} className="gap-2 shadow-[var(--shadow-elevated)]">
            <Eye className="w-4 h-4" /> Publier En Ligne
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <div className="space-y-6">
          
          {/* Visibilité et Ordre */}
          <Card>
            <CardHeader>
              <CardTitle>Disponibilité Principale</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between bg-muted/20 p-4 border border-dashed rounded-xl mb-4">
                <div>
                  <Label className="text-base font-bold text-muted-foreground">Affichage sur l'accueil</Label>
                  <p className="text-xs text-muted-foreground">Géré depuis le <strong>Centre de Contrôle (⚙️)</strong>.</p>
                </div>
                <Settings className="w-5 h-5 text-muted-foreground" />
              </div>
              <div className="pt-4 border-t flex items-center justify-between">
                <div>
                  <Label>Position actuelle : {mapIndex + 1}</Label>
                  <p className="text-xs text-muted-foreground">Modifiez l'ordre d'affichage vertical</p>
                </div>
                <div className="flex gap-2">
                  <Button variant="outline" size="icon" onClick={() => moveMap(-1)} disabled={mapIndex <= 0}>
                    <ArrowUp className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="icon" onClick={() => moveMap(1)} disabled={mapIndex >= layout.length - 1}>
                    <ArrowDown className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Module Territoriales */}
          <Card className="border-accent">
            <CardHeader className="bg-accent/5 rounded-t-xl pb-4">
              <CardTitle className="text-accent flex items-center gap-2">
                Élections Territoriales <Badge variant="outline">2026</Badge>
              </CardTitle>
              <CardDescription>Active la sous-catégorisation communale ou provinciale et propose le choix du type d'élection lors du clic.</CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <Label className="text-base font-bold">Activer les Sélecteurs (Provinces/Type)</Label>
                <Switch 
                  checked={mapContent.territoriales === "true"} 
                  onCheckedChange={(c) => updateContent("territoriales", c ? "true" : "false")} 
                />
              </div>
            </CardContent>
          </Card>

          {/* Model Selection */}
          <Card>
            <CardHeader>
              <CardTitle>Modèle Esthétique (Design)</CardTitle>
              <CardDescription>Sélectionnez comment visualiser les 12 régions marocaines.</CardDescription>
            </CardHeader>
            <CardContent>
              <RadioGroup 
                value={mapContent.model || "network"} 
                onValueChange={(v) => updateContent("model", v)}
                className="grid grid-cols-1 md:grid-cols-3 gap-4"
              >
                <div>
                  <RadioGroupItem value="network" id="r-network" className="peer sr-only" />
                  <Label htmlFor="r-network" className="flex flex-col items-center justify-center rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer text-center">
                    <span className="font-bold mb-2">Réseau Abstrait</span>
                    <span className="text-xs text-muted-foreground font-normal">Connecté via des nœuds géographiques.</span>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem value="grid" id="r-grid" className="peer sr-only" />
                  <Label htmlFor="r-grid" className="flex flex-col items-center justify-center rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer text-center">
                    <span className="font-bold mb-2">Grille (Cards)</span>
                    <span className="text-xs text-muted-foreground font-normal">Vue data-viz très "dashboard".</span>
                  </Label>
                </div>
                <div>
                  <RadioGroupItem value="list" id="r-list" className="peer sr-only" />
                  <Label htmlFor="r-list" className="flex flex-col items-center justify-center rounded-md border-2 border-muted bg-popover p-4 hover:bg-accent hover:text-accent-foreground peer-data-[state=checked]:border-primary [&:has([data-state=checked])]:border-primary cursor-pointer text-center">
                    <span className="font-bold mb-2">Liste Accordéon</span>
                    <span className="text-xs text-muted-foreground font-normal">Idéal pour l'accessibilité.</span>
                  </Label>
                </div>
              </RadioGroup>
            </CardContent>
          </Card>

          {/* Textes et Informations */}
          <Card>
            <CardHeader>
              <CardTitle>Textes d'Introduction</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <Label>Titre Principal (Français)</Label>
                <Input value={mapContent.title_fr || ""} onChange={e => updateContent("title_fr", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Titre Principal (Arabe)</Label>
                <Input dir="rtl" value={mapContent.title_ar || ""} onChange={e => updateContent("title_ar", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Sous-titre (Français)</Label>
                <Textarea value={mapContent.subtitle_fr || ""} onChange={e => updateContent("subtitle_fr", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Sous-titre (Arabe)</Label>
                <Textarea dir="rtl" value={mapContent.subtitle_ar || ""} onChange={e => updateContent("subtitle_ar", e.target.value)} />
              </div>
            </CardContent>
          </Card>

          {/* Custom Buttons */}
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle>Boutons Personnalisés</CardTitle>
                <CardDescription>Ajoutez des liens complémentaires sous le texte d'aide.</CardDescription>
              </div>
              <Button size="sm" variant="outline" onClick={addLink}>
                <Plus className="w-4 h-4 mr-2" /> Ajouter
              </Button>
            </CardHeader>
            <CardContent className="space-y-4">
              {customLinks.map((link, idx) => (
                <div key={idx} className="flex flex-col gap-3 p-4 rounded-xl border relative">
                  <Button 
                    variant="ghost" 
                    size="icon" 
                    className="absolute top-2 right-2 text-destructive"
                    onClick={() => removeLink(idx)}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                  
                  <div className="grid grid-cols-2 gap-4 mt-4">
                    <div className="space-y-1">
                      <Label className="text-xs">Libellé (Français)</Label>
                      <Input value={link.label_fr} onChange={e => updateLink(idx, "label_fr", e.target.value)} />
                    </div>
                    <div className="space-y-1">
                      <Label className="text-xs">Libellé (Arabe)</Label>
                      <Input dir="rtl" value={link.label_ar} onChange={e => updateLink(idx, "label_ar", e.target.value)} />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs">URL (Lien)</Label>
                    <Input placeholder="https://..." value={link.url} onChange={e => updateLink(idx, "url", e.target.value)} />
                  </div>
                </div>
              ))}
              {customLinks.length === 0 && (
                <p className="text-center text-sm text-muted-foreground py-4 border border-dashed rounded-lg">Aucun lien personnalisé ajouté.</p>
              )}
            </CardContent>
          </Card>

          {/* Regional Stats */}
          <Card className="border-primary/20">
            <CardHeader className="bg-primary/5 rounded-t-xl pb-4">
              <div className="flex justify-between items-center">
                <div>
                  <CardTitle className="text-primary">Données Spécifiques par Région</CardTitle>
                  <CardDescription>Oubliez les valeurs par défaut ! Ajoutez vos propres chiffres et cartes de statistiques.</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-6 space-y-6">
              <div className="space-y-2">
                <Label className="text-sm font-bold">Sélectionnez la Région à modifier</Label>
                <Select value={selectedRegionId} onValueChange={setSelectedRegionId}>
                  <SelectTrigger className="w-full">
                    <SelectValue placeholder="Choisir une région" />
                  </SelectTrigger>
                  <SelectContent>
                    {regionsMapData.map(r => (
                      <SelectItem key={r.id} value={r.id}>{r.name} ({r.nameAr})</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-4">
                  <h4 className="text-sm font-bold">Statistiques pour cette région</h4>
                  <Button size="sm" variant="outline" onClick={addStatToRegion} className="text-xs h-8">
                    <Plus className="w-3 h-3 mr-1" /> Ajouter
                  </Button>
                </div>

                <div className="space-y-4">
                  {getCurrentRegionStats().map((stat, idx) => (
                    <div key={idx} className="p-3 bg-muted/30 rounded-lg border relative">
                      <Button variant="ghost" size="icon" className="absolute top-1 right-1 h-6 w-6 text-destructive" onClick={() => removeStat(idx)}>
                        <Trash2 className="w-3 h-3" />
                      </Button>
                      <div className="grid grid-cols-2 gap-3 mt-4">
                        <div className="space-y-1">
                          <Label className="text-[10px] uppercase text-muted-foreground">Titre (Fr)</Label>
                          <Input className="h-8 text-xs" placeholder="ex: Inscrits" value={stat.title_fr} onChange={e => updateStat(idx, "title_fr", e.target.value)} />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] uppercase text-muted-foreground">Titre (Ar)</Label>
                          <Input dir="rtl" className="h-8 text-xs" placeholder="المسجلون" value={stat.title_ar} onChange={e => updateStat(idx, "title_ar", e.target.value)} />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] uppercase text-muted-foreground">Valeur (Fr)</Label>
                          <Input className="h-8 text-xs font-bold" placeholder="1.9 M" value={stat.value_fr} onChange={e => updateStat(idx, "value_fr", e.target.value)} />
                        </div>
                        <div className="space-y-1">
                          <Label className="text-[10px] uppercase text-muted-foreground">Valeur (Ar)</Label>
                          <Input dir="rtl" className="h-8 text-xs font-bold" placeholder="١.٩ م" value={stat.value_ar} onChange={e => updateStat(idx, "value_ar", e.target.value)} />
                        </div>
                      </div>
                    </div>
                  ))}
                  {getCurrentRegionStats().length === 0 && (
                    <div className="text-center p-6 border border-dashed rounded-lg bg-background">
                      <p className="text-sm text-muted-foreground">Les statistiques fixes par défaut sont actuellement affichées.<br/>Ajoutez une carte pour écraser l'affichage standard.</p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

        </div>

        {/* Live Preview Panel */}
        <div>
          <div className="sticky top-20 border rounded-2xl overflow-hidden shadow-xl bg-background/50">
            <div className="bg-primary/10 text-primary px-4 py-2 text-sm font-bold flex justify-between items-center border-b border-primary/20">
              <span className="flex items-center gap-2"><Eye className="w-4 h-4" /> Prévisualisation en direct</span>
              <Badge variant="outline" className="border-primary text-primary">Vue Utilisateur</Badge>
            </div>
            
            <div className="max-h-[800px] overflow-y-auto">
              {/* Wrapping inside a small container to mock full width but scaled down. But since we use responsive classes, it works elegantly. */}
              <ElectoralMap previewData={{...mapContent, custom_links: JSON.stringify(customLinks)}} />
            </div>
            
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminMapPage;
